import { Injectable } from '@angular/core';
import { FuseMockApiService, FuseMockApiUtils } from '@fuse/lib/mock-api';
import { brands as brandsData, categories as categoriesData, empleados as empleadosData, tags as tagsData, vendors as vendorsData } from 'app/mock-api/apps/empleado/data';
import { assign, cloneDeep } from 'lodash-es';

@Injectable({
    providedIn: 'root',
})
export class EmpleadoMockApi
{
    private _categories: any[] = categoriesData;
    private _brands: any[] = brandsData;
    private _empleados: any[] = empleadosData;
    private _tags: any[] = tagsData;
    private _vendors: any[] = vendorsData;

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService)
    {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void
    {
        // -----------------------------------------------------------------------------------------------------
        // @ Categories - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/categories')
            .reply(() => [200, cloneDeep(this._categories)]);

        // -----------------------------------------------------------------------------------------------------
        // @ Brands - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/brands')
            .reply(() => [200, cloneDeep(this._brands)]);

        // -----------------------------------------------------------------------------------------------------
        // @ Empleados - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/empleados', 300)
            .reply(({request}) =>
            {

                // Get available queries
                const search = request.params.get('search');
                const sort = request.params.get('sort') || 'name';
                const order = request.params.get('order') || 'asc';
                const page = parseInt(request.params.get('page') ?? '1', 10);
                const size = parseInt(request.params.get('size') ?? '10', 10);

                // Clone the empleados
                let empleados: any[] | null = cloneDeep(this._empleados);

                // Sort the empleados
                if ( sort === 'sku' || sort === 'name' || sort === 'active' )
                {
                    empleados.sort((a, b) =>
                    {
                        const fieldA = a[sort].toString().toUpperCase();
                        const fieldB = b[sort].toString().toUpperCase();
                        return order === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
                    });
                }
                else
                {
                    empleados.sort((a, b) => order === 'asc' ? a[sort] - b[sort] : b[sort] - a[sort]);
                }

                // If search exists...
                if ( search )
                {
                    // Filter the empleados
                    empleados = empleados.filter(contact => contact.name && contact.name.toLowerCase().includes(search.toLowerCase()));
                }

                // Paginate - Start
                const empleadosLength = empleados.length;

                // Calculate pagination details
                const begin = page * size;
                const end = Math.min((size * (page + 1)), empleadosLength);
                const lastPage = Math.max(Math.ceil(empleadosLength / size), 1);

                // Prepare the pagination object
                let pagination = {};

                // If the requested page number is bigger than
                // the last possible page number, return null for
                // empleados but also send the last possible page so
                // the app can navigate to there
                if ( page > lastPage )
                {
                    empleados = null;
                    pagination = {
                        lastPage,
                    };
                }
                else
                {
                    // Paginate the results by size
                    empleados = empleados.slice(begin, end);

                    // Prepare the pagination mock-api
                    pagination = {
                        length    : empleadosLength,
                        size      : size,
                        page      : page,
                        lastPage  : lastPage,
                        startIndex: begin,
                        endIndex  : end - 1,
                    };
                }

                // Return the response
                return [
                    200,
                    {
                        empleados,
                        pagination,
                    },
                ];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Empleado - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/empleado')
            .reply(({request}) =>
            {

                // Get the id from the params
                const id = request.params.get('id');

                // Clone the empleados
                const empleados = cloneDeep(this._empleados);

                // Find the empleado
                const empleado = empleados.find(item => item.id === id);

                // Return the response
                return [200, empleado];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Empleado - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/apps/empleado/empleado')
            .reply(() =>
            {

                // Generate a new empleado
                const newEmpleado = {
                    id         : FuseMockApiUtils.guid(),
                    category   : '',
                    name       : 'A New Empleado',
                    description: '',
                    tags       : [],
                    sku        : '',
                    barcode    : '',
                    brand      : '',
                    vendor     : '',
                    stock      : '',
                    reserved   : '',
                    cost       : '',
                    basePrice  : '',
                    taxPercent : '',
                    price      : '',
                    weight     : '',
                    thumbnail  : '',
                    images     : [],
                    active     : false,
                };

                // Unshift the new empleado
                this._empleados.unshift(newEmpleado);

                // Return the response
                return [200, newEmpleado];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Empleado - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/empleado/empleado')
            .reply(({request}) =>
            {

                // Get the id and empleado
                const id = request.body.id;
                const empleado = cloneDeep(request.body.empleado);

                // Prepare the updated empleado
                let updatedEmpleado = null;

                // Find the empleado and update it
                this._empleados.forEach((item, index, empleados) =>
                {

                    if ( item.id === id )
                    {
                        // Update the empleado
                        empleados[index] = assign({}, empleados[index], empleado);

                        // Store the updated empleado
                        updatedEmpleado = empleados[index];
                    }
                });

                // Return the response
                return [200, updatedEmpleado];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Empleado - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/empleado/empleado')
            .reply(({request}) =>
            {

                // Get the id
                const id = request.params.get('id');

                // Find the empleado and delete it
                this._empleados.forEach((item, index) =>
                {

                    if ( item.id === id )
                    {
                        this._empleados.splice(index, 1);
                    }
                });

                // Return the response
                return [200, true];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Tags - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/tags')
            .reply(() => [200, cloneDeep(this._tags)]);

        // -----------------------------------------------------------------------------------------------------
        // @ Tags - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/apps/empleado/tag')
            .reply(({request}) =>
            {

                // Get the tag
                const newTag = cloneDeep(request.body.tag);

                // Generate a new GUID
                newTag.id = FuseMockApiUtils.guid();

                // Unshift the new tag
                this._tags.unshift(newTag);

                // Return the response
                return [200, newTag];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Tags - PATCH
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPatch('api/apps/empleado/tag')
            .reply(({request}) =>
            {

                // Get the id and tag
                const id = request.body.id;
                const tag = cloneDeep(request.body.tag);

                // Prepare the updated tag
                let updatedTag = null;

                // Find the tag and update it
                this._tags.forEach((item, index, tags) =>
                {

                    if ( item.id === id )
                    {
                        // Update the tag
                        tags[index] = assign({}, tags[index], tag);

                        // Store the updated tag
                        updatedTag = tags[index];
                    }
                });

                // Return the response
                return [200, updatedTag];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Tag - DELETE
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onDelete('api/apps/empleado/tag')
            .reply(({request}) =>
            {

                // Get the id
                const id = request.params.get('id');

                // Find the tag and delete it
                this._tags.forEach((item, index) =>
                {

                    if ( item.id === id )
                    {
                        this._tags.splice(index, 1);
                    }
                });

                // Get the empleados that have the tag
                const empleadosWithTag = this._empleados.filter(empleado => empleado.tags.indexOf(id) > -1);

                // Iterate through them and delete the tag
                empleadosWithTag.forEach((empleado) =>
                {
                    empleado.tags.splice(empleado.tags.indexOf(id), 1);
                });

                // Return the response
                return [200, true];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Vendors - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/apps/empleado/vendors')
            .reply(() => [200, cloneDeep(this._vendors)]);
    }
}
